import React, { useEffect, useState } from "react";
import PatientService from "../../Services/PatientService";
import dayjs from "dayjs";
import { Modal, Button, Spin } from "antd";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import HRV from "../../assets/images/Svg/Hrv.svg";

const HRVData = () => {
  const [smartWatchId, setSmartWatchId] = useState("");
  const [dailies, setDailies] = useState({});
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const patJson = localStorage.getItem("patientData");
  const patObj = patJson ? JSON.parse(patJson) : null;

  const openGraph = () => setIsModalOpen(true);
  const closeGraph = () => setIsModalOpen(false);

  useEffect(() => {
    getSmartWatchId();
  }, []);

  const getSmartWatchId = async () => {
    setLoading(true);
    try {
      const patientRes = await PatientService.getPatient({
        patientId: patObj.patientId,
      });
      const userId = patientRes?.data?.[0]?.smartWatchUserId;
      if (!userId) return;
      setSmartWatchId(userId);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (smartWatchId) fetchWatchData();
  }, [smartWatchId]);

  const fetchWatchData = async (fromDate, toDate) => {
    const start = fromDate
      ? dayjs(fromDate).format("DD/MM/YYYY")
      : dayjs().format("DD/MM/YYYY");
    const end = toDate
      ? dayjs(toDate).format("DD/MM/YYYY")
      : dayjs().format("DD/MM/YYYY");
    const payload = {
      type: "DAILIES",
      userId: smartWatchId,
      fromDate: start,
      toDate: end,
      limit: 100,
      offset: 0,
    };

    try {
      setLoading(true);
      const res = await PatientService.patientWatchData(payload);
      if (res?.data?.length > 0) {
        const rawJson = JSON.parse(res.data[0].RAW_JSON || "{}");
        setDailies(rawJson);
      } else setDailies({});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        title="click to view Graph"
        className="px-2 cursor-pointer"
        onClick={openGraph}
      >
        <div className="bg-white shadow rounded-lg p-4 w-48 h-40">
          <div className="flex items-center gap-2 self-start mb-4">
            <img src={HRV} alt="icon" />
            <h2 className="text-sm font-sans font-semibold">HRV Status</h2>
          </div>
          <div className="flex justify-between mt-3 text-sm">
            <p className="font-semibold">
              {dailies?.restingHeartRateInBeatsPerMinute ?? "-"} ms{" "}
              <span className="text-gray-500 text-xs">Resting</span>
            </p>
            <p className="font-semibold">
              {dailies?.maxHeartRateInBeatsPerMinute ?? "-"} ms{" "}
              <span className="text-gray-500 text-xs">Max</span>
            </p>
          </div>
        </div>
      </div>

      {smartWatchId && (
        <HRVModal
          open={isModalOpen}
          onClose={closeGraph}
          smartWatchId={smartWatchId}
        />
      )}
    </>
  );
};

const HRVModal = ({ open, onClose, smartWatchId }) => {
  const [tab, setTab] = useState("1D");
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [chartData1D, setChartData1D] = useState([]);
  const [chartData7D, setChartData7D] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch 1D data (timeOffsetHeartRateSamples)
  const fetch1DData = async (date) => {
    if (!smartWatchId) return;
    const start = dayjs(date).format("DD/MM/YYYY");
    const payload = {
      type: "DAILIES",
      userId: smartWatchId,
      fromDate: start,
      toDate: start,
      limit: 1,
      offset: 0,
    };
    try {
      setLoading(true);
      const res = await PatientService.patientWatchData(payload);
      if (res?.data?.length > 0) {
        const raw = JSON.parse(res.data[0].RAW_JSON || "{}");
        const samples = raw?.timeOffsetHeartRateSamples || {};
        const data = Object.entries(samples).map(([time, value]) => ({
          time: dayjs(time * 1000)
            .utc()
            .format("hh:mm"),
          value,
        }));
        setChartData1D(data);
      } else setChartData1D([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch 7D data (heartRate in the object outside RAW JSON)
  const fetch7DData = async (startDate, endDate) => {
    if (!smartWatchId) return;
    const start = dayjs(startDate).format("DD/MM/YYYY");
    const end = dayjs(endDate).format("DD/MM/YYYY");
    const payload = {
      type: "DAILIES",
      userId: smartWatchId,
      fromDate: start,
      toDate: end,
      limit: 100,
      offset: 0,
    };
    try {
      setLoading(true);
      const res = await PatientService.patientWatchData(payload);
      if (res?.data?.length > 0) {
        const data = res.data.map((item) => {
          const date = dayjs(item.CALENDAR_DATE).local().format("DD/MM/YYYY");
          const raw = JSON.parse(item.RAW_JSON || "{}");
          return {
            date,
            heartRate: item.heartRate ?? 0,
            restingHeartRateInBeatsPerMinute:
              raw.restingHeartRateInBeatsPerMinute ?? 0,
            maxHeartRateInBeatsPerMinute: raw.maxHeartRateInBeatsPerMinute ?? 0,
          };
        });
        setChartData7D(data);
      } else setChartData7D([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "1D") {
      fetch1DData(selectedDate);
    } else {
      const end = dayjs();
      const start = end.subtract(6, "day");
      fetch7DData(start, end);
    }
  }, [tab, selectedDate, smartWatchId]);

  const handlePrevDay = () => setSelectedDate(selectedDate.subtract(1, "day"));
  const handleNextDay = () => setSelectedDate(selectedDate.add(1, "day"));

  const handlePrev7D = () => {
    const start = dayjs(chartData7D[0]?.date, "DD/MM").subtract(7, "day");
    const end = dayjs(chartData7D[0]?.date, "DD/MM").subtract(1, "day");
    fetch7DData(start, end);
  };

  const handleNext7D = () => {
    const end = dayjs(chartData7D[chartData7D.length - 1]?.date, "DD/MM").add(
      7,
      "day"
    );
    const start = end.subtract(6, "day");
    fetch7DData(start, end);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      title="HRV Summary"
    >
      <div>
        <div className="flex justify-center mb-4 gap-3">
          <Button
            type={tab === "1D" ? "primary" : "default"}
            onClick={() => setTab("1D")}
          >
            1D
          </Button>
          <Button
            type={tab === "7D" ? "primary" : "default"}
            onClick={() => setTab("7D")}
          >
            7D
          </Button>
        </div>

        {loading && <Loader />}

        {tab === "1D" && (
          <>
            <div className="flex items-center justify-center gap-4 mb-4">
              <Button onClick={handlePrevDay}>← Prev Day</Button>
              <span className="font-semibold">
                {selectedDate.format("DD/MM/YYYY")}
              </span>
              <Button onClick={handleNextDay}>Next Day →</Button>
            </div>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData1D}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#ef4444"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {tab === "7D" && (
          <>
            <div className="flex justify-between mb-4">
              <Button onClick={handlePrev7D}>← Prev 7 Days</Button>
              <Button onClick={handleNext7D}>Next 7 Days →</Button>
            </div>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData7D}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="heartRate"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-4 md:grid-cols-7 gap-3 mb-5">
              {chartData7D.map((item, idx) => {
                const dayName = item?.date
                  ? dayjs(item.date, "DD/MM/YYYY").format("dddd")
                  : "";
                const resting = item.restingHeartRateInBeatsPerMinute ?? 0;
                const max = item.maxHeartRateInBeatsPerMinute ?? 0;

                return (
                  <div
                    key={idx}
                    className="bg-white shadow rounded-lg p-3 flex flex-col items-center gap-2"
                  >
                    <p className="text-xs text-gray-500">{dayName}</p>

                    <div className="flex flex-col items-center justify-center gap-2 w-full">
                      <div className="flex items-center gap-2 w-full px-2">
                        <p className="text-xs text-gray-500">Resting Avg</p>
                        <p className="text-sm font-semibold text-red-500">
                          {resting} ms
                        </p>
                      </div>
                      <div className="flex items-center gap-2 w-full px-2">
                        <p className="text-xs text-gray-500">Max Avg</p>
                        <p className="text-sm font-semibold text-blue-500">
                          {max} ms
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const Loader = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm z-50">
    <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

export default HRVData;
